import React, { useEffect, useState, useRef } from 'react';
import { keyframes } from '@emotion/react';
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearPortalSessionStorage } from '../utils/sessionInvalidation';
import PageLayout from '../components/common/PageLayout';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ENABLED = RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY.trim() !== '';

const publicBase = `${String(process.env.PUBLIC_URL || '').replace(/\/$/, '')}`;

const SLIDE_MS = 5000;
/** Opacity crossfade between slides (incoming fades in while outgoing fades out). */
const SLIDE_CROSSFADE_MS = 1000;
/** Ken Burns–style zoom on the visible slide only (slow, no blur). */
const LOGIN_PROMO_IMAGE_ZOOM_S = 14;

const loginPromoImageFloat = keyframes`
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.07);
  }
`;
/** Same family as PageLayout org strip — light cream / warm white (no charcoal). */
const LOGIN_PROMO_PANEL_BG =
  'linear-gradient(180deg, rgba(255, 255, 255, 0.97) 0%, rgba(255, 252, 242, 0.96) 52%, rgba(255, 248, 225, 0.94) 100%)';
const LOGIN_PROMO_BORDER = 'rgba(227, 207, 142, 0.55)';

const LOGIN_LOGO_SIVA = `${publicBase}/assets/images/Siva_logo.png`;
const LOGIN_LOGO_VENKATESWARA = `${publicBase}/assets/images/Venkateswara_logo.png`;

const LOGIN_PROMO_SLIDES = [
  {
    id: 'how-to-help',
    title: 'How to support your Temple?',
    image: LOGIN_LOGO_SIVA,
    caption:
      'Annual Poshaka, temple events & services, featured projects, general donation, and volunteering—every gift helps daily operations. Be a supporter of your Temple and fulfill your duty of supporting Sanatana Dharma.',
    href: 'https://icctmemphis.org/donate/how-to-help/',
  },
  {
    id: 'poshaka',
    title: 'Poshaka Parivar',
    image: LOGIN_LOGO_VENKATESWARA,
    caption:
      'An annual membership that helps sustain the temple. Your support funds day-to-day operations and maintenance so we can serve the community.',
    href: 'https://icctmemphis.org/about-temple/poshaka-parivar/',
  },
  {
    id: 'annadanam',
    title: 'Annadanam',
    image: LOGIN_LOGO_SIVA,
    caption: 'Annadanam Samam Danam Trilokeshu Na Vidhathe',
    href: 'https://icctmemphis.org/donate/annadanam/',
  },
  {
    id: 'kotilinga',
    title: 'Kotilingalingita Kailasa Prastharam (Kotilinga Kshetram)',
    image: LOGIN_LOGO_VENKATESWARA,
    caption:
      'A unique and holy global project at ICCT—Kotilinga Kshetram—inviting devotees to take part in this historic Kailasa Prastharam and its blessings.',
    href: 'https://icctmemphis.org/kotilinga-kshetram/significance/',
  },
];

function LoginPromoCarousel({ slides, intervalMs }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const t = window.setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, intervalMs);
    return () => window.clearInterval(t);
  }, [slides.length, intervalMs, paused]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 300, sm: 360, md: 'min(520px, calc(100dvh - 280px))' },
        minHeight: { md: 420 },
        borderRadius: 3,
        overflow: 'hidden',
        background: LOGIN_PROMO_PANEL_BG,
        border: '1px solid',
        borderColor: LOGIN_PROMO_BORDER,
        boxShadow: '0 14px 34px rgba(161, 122, 38, 0.14)',
      }}
    >
      <Box sx={{ position: 'absolute', inset: 0 }}>
        {slides.map((slide, i) => (
          <Box
            key={slide.id}
            onClick={() => setPaused((p) => !p)}
            role="presentation"
            title={i === index ? (paused ? 'Click to play slideshow' : 'Click to pause slideshow') : undefined}
            sx={{
              position: 'absolute',
              inset: 0,
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              opacity: i === index ? 1 : 0,
              transitionProperty: 'opacity',
              transitionDuration: `${SLIDE_CROSSFADE_MS}ms`,
              transitionTimingFunction: 'ease-in-out',
              willChange: 'opacity',
              WebkitBackfaceVisibility: 'hidden',
              backfaceVisibility: 'hidden',
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
              },
              pointerEvents: i === index ? 'auto' : 'none',
              zIndex: i === index ? 1 : 0,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 1.5,
                borderRadius: 2,
                overflow: 'hidden',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255, 252, 245, 0.45)',
              }}
            >
              <Box
                component="img"
                src={slide.image}
                alt=""
                sx={{
                  height: '60%',
                  width: 'auto',
                  maxWidth: '90%',
                  objectFit: 'contain',
                  objectPosition: 'center',
                  display: 'block',
                  filter: 'none',
                  transformOrigin: 'center',
                  animation:
                    i === index
                      ? `${loginPromoImageFloat} ${LOGIN_PROMO_IMAGE_ZOOM_S}s ease-in-out infinite alternate`
                      : 'none',
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  },
                }}
              />
            </Box>
            <Typography
              variant="h4"
              sx={{
                position: 'relative',
                zIndex: 1,
                p: 2.5,
                pb: 1,
                fontWeight: 800,
                color: 'primary.dark',
                letterSpacing: 0.2,
                textShadow:
                  '0 1px 0 rgba(255,255,255,0.95), 0 0 12px rgba(255,252,245,0.9), 0 1px 3px rgba(255,255,255,0.85)',
              }}
            >
              {slide.title}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'flex-end' }}
              justifyContent="space-between"
              spacing={1.5}
              sx={{
                position: 'relative',
                zIndex: 1,
                p: 2.5,
                pt: 2,
                pb: { xs: 5, sm: 5 },
                gap: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.5,
                  textShadow: '0 1px 0 rgba(255,255,255,0.9), 0 0 8px rgba(255,252,245,0.75)',
                  flex: { sm: '1 1 0' },
                  minWidth: 0,
                  pr: { sm: 1 },
                }}
              >
                {slide.caption}
              </Typography>
              <Button
                component="a"
                href={slide.href}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="medium"
                endIcon={<OpenInNewIcon />}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  flexShrink: 0,
                  alignSelf: { xs: 'stretch', sm: 'flex-end' },
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 2,
                  boxShadow: '0 4px 14px rgba(161, 122, 38, 0.2)',
                  bgcolor: 'rgba(255,255,255,0.98)',
                  color: 'primary.dark',
                  '&:hover': { bgcolor: 'common.white' },
                }}
              >
                More details
              </Button>
            </Stack>
          </Box>
        ))}
      </Box>

      <Stack
        direction="row"
        spacing={0.75}
        justifyContent="center"
        sx={{
          position: 'absolute',
          bottom: 10,
          left: 0,
          right: 0,
          zIndex: 4,
          pointerEvents: 'auto',
        }}
      >
        {slides.map((s, i) => (
          <IconButton
            key={s.id}
            size="small"
            onClick={() => setIndex(i)}
            aria-label={`Show ${s.title}`}
            sx={{
              p: 0.5,
              color: i === index ? 'primary.dark' : 'rgba(26, 35, 126, 0.35)',
            }}
          >
            <Box
              sx={{
                width: i === index ? 22 : 8,
                height: 8,
                borderRadius: 4,
                bgcolor: 'currentColor',
                transition: 'width 0.35s ease, opacity 0.35s ease',
              }}
            />
          </IconButton>
        ))}
      </Stack>
    </Box>
  );
}

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [code, setCode] = useState('');
  const [expiresIn, setExpiresIn] = useState(300);
  const [awaitingOtp, setAwaitingOtp] = useState(false);
  const [error, setError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [showOtpResendRecaptcha, setShowOtpResendRecaptcha] = useState(false);
  const recaptchaRef = useRef(null);
  const location = useLocation();
  const { requestOtp, verifyOtpLocal, logout, isAuthenticated, staff } = useAuth();
  const navigate = useNavigate();
  const [sessionNotice, setSessionNotice] = useState(() => location.state?.authMessage || '');

  useEffect(() => {
    if (location.state?.email) {
      setEmail(String(location.state.email || ''));
      setAwaitingOtp(true);
      setExpiresIn(300);
      navigate('/login', { replace: true, state: { authMessage: location.state?.authMessage || '' } });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (location.state?.authMessage) {
      setSessionNotice(location.state.authMessage);
      navigate('/login', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    (async () => {
      try {
        clearPortalSessionStorage();
        await logout();
      } catch {}
    })();
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated || !staff?.subject) return;
    let dest = null;
    try {
      const u = sessionStorage.getItem('icct_donate_post_login_redirect');
      if (u && u.startsWith('/')) {
        dest = u;
        sessionStorage.removeItem('icct_donate_post_login_redirect');
      }
    } catch {
      /* ignore */
    }
    if (dest) {
      navigate(dest, { replace: true });
      return;
    }
    navigate(`/devotees/${staff.subject}/home`, { replace: true });
  }, [isAuthenticated, staff?.subject, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.trim()) {
      setError('Email is required');
      return;
    }

    if (RECAPTCHA_ENABLED && !recaptchaToken) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    try {
      setLoading(true);
      await requestOtp(email.trim(), recaptchaToken);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
      setShowOtpResendRecaptcha(false);
      setAwaitingOtp(true);
      setExpiresIn(300);
      setCode('');
    } catch (err) {
      setError(err?.message || 'Failed to send OTP');
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    setError('');
  };

  useEffect(() => {
    if (!awaitingOtp) return undefined;
    const timer = window.setInterval(() => {
      setExpiresIn((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [awaitingOtp]);

  useEffect(() => {
    if (awaitingOtp && expiresIn === 0) {
      setAwaitingOtp(false);
      setCode('');
      setError('OTP expired. Please request a new code.');
    }
  }, [awaitingOtp, expiresIn]);

  const onVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Email is required');
      setAwaitingOtp(false);
      return;
    }
    if (!code.trim()) {
      setError('OTP code is required');
      return;
    }
    try {
      setOtpLoading(true);
      await verifyOtpLocal({ email: email.trim(), userInputCode: code.trim() });
    } catch (err) {
      setError(err?.message || 'Invalid OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const onResendOtp = async () => {
    setError('');
    if (!email.trim()) {
      setAwaitingOtp(false);
      setError('Email is required');
      return;
    }
    if (RECAPTCHA_ENABLED && !recaptchaToken) {
      setShowOtpResendRecaptcha(true);
      setError('Please complete the reCAPTCHA verification to resend OTP');
      return;
    }
    try {
      setResending(true);
      await requestOtp(email.trim(), recaptchaToken);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
      setShowOtpResendRecaptcha(false);
      setExpiresIn(300);
      setCode('');
    } catch (err) {
      setError(err?.message || 'Failed to resend OTP');
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setResending(false);
    }
  };

  const onBackToLogin = () => {
    setAwaitingOtp(false);
    setCode('');
    setError('');
    setExpiresIn(300);
    setRecaptchaToken(null);
    setShowOtpResendRecaptcha(false);
    if (recaptchaRef.current) recaptchaRef.current.reset();
  };

  return (
    <PageLayout contentMaxWidth={false} showHeaderImage={false} portalTitle="" contentPadding={{ xs: 1, sm: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column-reverse', md: 'row' },
          alignItems: { md: 'flex-start' },
          gap: { xs: 2, md: 0 },
        }}
      >
        <Box
          sx={{
            flex: { md: '0 0 60%' },
            width: { xs: '100%', md: '60%' },
            minWidth: 0,
            pr: { md: 2 },
            py: { xs: 0, md: 0 },
            display: 'flex',
            alignItems: { md: 'stretch' },
          }}
        >
          <LoginPromoCarousel slides={LOGIN_PROMO_SLIDES} intervalMs={SLIDE_MS} />
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: 'none', md: 'block' }, mx: 0, borderColor: 'divider' }}
        />
        <Divider sx={{ display: { xs: 'block', md: 'none' }, my: 0 }} />

        <Box
          sx={{
            flex: { md: '0 0 40%' },
            width: { xs: '100%', md: '40%' },
            minWidth: 0,
            display: 'flex',
            alignItems: { md: 'center' },
            justifyContent: 'center',
            pl: { md: 2 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 420,
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 800 }}>
              {awaitingOtp ? 'Enter OTP' : 'Login to Devotee Account'}
            </Typography>
            {sessionNotice ? (
              <Alert severity="info" sx={{ mb: 2 }} onClose={() => setSessionNotice('')}>
                {sessionNotice}
              </Alert>
            ) : null}
            {awaitingOtp ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Sent to: {email || 'your email'} • Expires in {Math.floor(expiresIn / 60)}:
                  {String(expiresIn % 60).padStart(2, '0')}
                </Typography>
                <Box component="form" onSubmit={onVerifyOtp}>
                  <TextField
                    fullWidth
                    label="OTP Code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.trim())}
                    margin="normal"
                    autoComplete="one-time-code"
                    autoFocus
                  />
                  {error ? (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                      {error}
                    </Typography>
                  ) : null}
                  {RECAPTCHA_ENABLED && showOtpResendRecaptcha ? (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={handleRecaptchaChange}
                        onExpired={() => setRecaptchaToken(null)}
                        onError={() => {
                          setRecaptchaToken(null);
                          setError('reCAPTCHA error. Please refresh the page and try again.');
                        }}
                      />
                    </Box>
                  ) : null}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    <Button variant="contained" type="submit" disabled={otpLoading || !code.trim()}>
                      {otpLoading ? 'Verifying...' : 'Verify'}
                    </Button>
                    <Button
                      variant="outlined"
                      type="button"
                      onClick={onResendOtp}
                      disabled={resending}
                    >
                      {resending ? 'Resending...' : 'Resend OTP'}
                    </Button>
                    <Button variant="text" color="inherit" type="button" onClick={onBackToLogin}>
                      Back to Login
                    </Button>
                  </Box>
                </Box>
              </>
            ) : (
              <Box component="form" onSubmit={onSubmit}>
                <TextField
                  type="email"
                  fullWidth
                  label="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin="normal"
                  helperText="Use the same email you use for ICCT donations. We will send a one-time code to log in."
                  autoComplete="email"
                  required
                />
                {error ? (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {error}
                  </Typography>
                ) : null}
                {RECAPTCHA_ENABLED ? (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptchaChange}
                      onExpired={() => setRecaptchaToken(null)}
                      onError={() => {
                        setRecaptchaToken(null);
                        setError('reCAPTCHA error. Please refresh the page and try again.');
                      }}
                    />
                  </Box>
                ) : null}
                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  disabled={loading || !email.trim() || (RECAPTCHA_ENABLED && !recaptchaToken)}
                  sx={{ mt: 2 }}
                >
                  {loading ? 'Sending...' : 'Send OTP'}
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default LoginPage;
